import { useEffect, useMemo, useRef, useState } from 'react'
import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Filter,
  Loader2,
  MessageSquareText,
  Mic,
  Pause,
  Phone,
  PhoneIncoming,
  PhoneMissed,
  Play,
  RotateCcw,
  RotateCw,
  Search,
  Volume2,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  useBatchCallDetail,
  useBatchCallLogs,
  type BatchCall,
  type BatchCallLog,
} from '@/api/batchCalls'
import { useAICallAudio } from '@/api/aiAgents'

interface Props {
  batchId: number
  onBack: () => void
  onRetry: () => void
  onCancel: () => void
}

type StatusFilter = 'all' | 'answered' | 'failed' | 'pending' | 'skipped'
const PAGE_SIZE = 10

export function BatchCallDetailsView({ batchId, onBack, onRetry, onCancel }: Props) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA'

  const { data: batch, isLoading: loadingBatch } = useBatchCallDetail(batchId)
  const { data: logs = [], isLoading: loadingLogs } = useBatchCallLogs(batchId)

  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedLog, setSelectedLog] = useState<BatchCallLog | null>(null)

  // Reset pagination quand filtres changent.
  useEffect(() => {
    setCurrentPage(1)
  }, [searchQuery, statusFilter])

  const stats = useMemo(() => {
    const answered = logs.filter((l) => l.call_status === 'answered').length
    const noAnswer = logs.filter((l) =>
      ['no_answer', 'busy', 'voicemail'].includes(l.call_status),
    ).length
    const failed = logs.filter((l) => l.call_status === 'failed').length
    const skipped = logs.filter((l) => l.call_status === 'skipped').length
    const total = logs.length
    const completed =
      total -
      logs.filter((l) => l.call_status === 'pending' || l.call_status === 'calling')
        .length
    const successRate = completed > 0 ? Math.round((answered / completed) * 100) : 0
    return {
      total,
      answered,
      noAnswer,
      failed,
      skipped,
      completed,
      successRate,
      answeredPct: total > 0 ? Math.round((answered / total) * 100) : 0,
      noAnswerPct: total > 0 ? Math.round((noAnswer / total) * 100) : 0,
      failedPct: total > 0 ? Math.round((failed / total) * 100) : 0,
    }
  }, [logs])

  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      if (searchQuery && !log.phone_number.includes(searchQuery)) return false
      if (statusFilter !== 'all') {
        if (statusFilter === 'answered' && log.call_status !== 'answered')
          return false
        if (
          statusFilter === 'failed' &&
          !['failed', 'no_answer', 'busy'].includes(log.call_status)
        )
          return false
        if (statusFilter === 'pending' && log.call_status !== 'pending')
          return false
        if (statusFilter === 'skipped' && log.call_status !== 'skipped')
          return false
      }
      return true
    })
  }, [logs, searchQuery, statusFilter])

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE))
  const paginated = filteredLogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  )

  const formatDuration = (s: number | null | undefined) => {
    if (!s) return '—'
    const m = Math.floor(s / 60)
    const sec = s % 60
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }

  const formatTime = (iso: string | null | undefined) => {
    if (!iso) return '—'
    return new Date(iso).toLocaleTimeString(dateLocale, {
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const handleExportCSV = () => {
    const headers = [
      t('manager.batchCalls.details.csv.number'),
      t('manager.batchCalls.details.csv.status'),
      t('manager.batchCalls.details.csv.duration'),
      t('manager.batchCalls.details.csv.callDate'),
      t('manager.batchCalls.details.csv.attempt'),
    ]
    const rows = filteredLogs.map((log) =>
      [
        log.phone_number,
        log.call_status_display || log.call_status,
        log.duration ?? 0,
        log.created_at ? new Date(log.created_at).toLocaleString(dateLocale) : '—',
        log.attempt_number,
      ].join(','),
    )
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `campagne-${batch?.name ?? batchId}-${
      new Date().toISOString().split('T')[0]
    }.csv`
    link.click()
    URL.revokeObjectURL(link.href)
  }

  const donutOffset = 502 - (502 * stats.successRate) / 100

  const status = batch?.status ?? ''
  const canCancel = status === 'scheduled' || status.includes('progress')
  const canRetry =
    status === 'completed' && (stats.noAnswer > 0 || stats.failed > 0)

  if (loadingBatch || loadingLogs) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="gap-2 flex-shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('manager.batchCalls.details.back')}
          </Button>
          <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white truncate">
            {batch?.name}
          </h2>
          {batch && <StatusBadge status={status} />}
        </div>
        <div className="flex items-center gap-2">
          {canCancel && (
            <Button type="button" variant="outline" size="sm" onClick={onCancel}>
              {t('manager.batchCalls.details.cancel')}
            </Button>
          )}
          {canRetry && (
            <Button
              type="button"
              size="sm"
              onClick={onRetry}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {t('manager.batchCalls.details.retry')}
            </Button>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            className="gap-2"
          >
            <Download className="w-4 h-4" />
            {t('manager.batchCalls.details.exportCsv')}
          </Button>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Phone className="w-4 h-4" />}
          label={t('manager.batchCalls.details.totalCalls')}
          value={stats.total}
        />
        <StatCard
          icon={<PhoneIncoming className="w-4 h-4" />}
          label={t('manager.batchCalls.details.answered')}
          value={stats.answered}
          pct={stats.answeredPct}
          tone="emerald"
        />
        <StatCard
          icon={<PhoneMissed className="w-4 h-4" />}
          label={t('manager.batchCalls.details.noAnswer')}
          value={stats.noAnswer}
          pct={stats.noAnswerPct}
          tone="amber"
        />
        <StatCard
          icon={<AlertCircle className="w-4 h-4" />}
          label={t('manager.batchCalls.details.failed')}
          value={stats.failed}
          pct={stats.failedPct}
          tone="red"
        />
      </div>

      {/* Donut + bar chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-6">
            {t('manager.batchCalls.details.successRate')}
          </h3>
          <div className="flex items-center justify-center relative">
            <svg
              className="transform -rotate-90"
              height="200"
              viewBox="0 0 200 200"
              width="200"
            >
              <circle
                className="text-slate-100 dark:text-slate-800"
                cx="100"
                cy="100"
                fill="none"
                r="80"
                stroke="currentColor"
                strokeWidth="20"
              />
              <circle
                className="text-emerald-500"
                cx="100"
                cy="100"
                fill="none"
                r="80"
                stroke="currentColor"
                strokeDasharray="502"
                strokeDashoffset={donutOffset}
                strokeLinecap="round"
                strokeWidth="20"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-4xl font-bold text-emerald-600 dark:text-emerald-400">
                {stats.successRate}%
              </span>
              <span className="text-xs text-slate-500 mt-1">
                {t('manager.batchCalls.details.success')}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-6">
            {t('manager.batchCalls.details.statusDistribution')}
          </h3>
          <div className="flex items-end justify-between gap-3 pb-8 relative h-[200px]">
            <div className="absolute inset-0 flex flex-col justify-between pointer-events-none pb-8">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="border-t border-slate-100 dark:border-slate-800 w-full"
                />
              ))}
            </div>
            {[
              {
                label: t('manager.batchCalls.details.barAnswered'),
                value: stats.answered,
                color: 'bg-emerald-500',
              },
              {
                label: t('manager.batchCalls.details.barNoAnswer'),
                value: stats.noAnswer,
                color: 'bg-amber-500',
              },
              {
                label: t('manager.batchCalls.details.barFailed'),
                value: stats.failed,
                color: 'bg-red-500',
              },
              {
                label: t('manager.batchCalls.details.barSkipped'),
                value: stats.skipped,
                color: 'bg-slate-400',
              },
            ].map((bar) => {
              const pct =
                stats.total > 0
                  ? Math.max((bar.value / stats.total) * 100, 2)
                  : 2
              return (
                <div
                  key={bar.label}
                  className="flex-1 flex flex-col items-center gap-1 z-10"
                >
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                    {bar.value}
                  </span>
                  <div
                    className={cn('w-full rounded-t-sm transition-all', bar.color)}
                    style={{
                      height: `${pct}%`,
                      minHeight: '4px',
                      maxHeight: '160px',
                    }}
                  />
                  <span className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
                    {bar.label}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Journal */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t('manager.batchCalls.details.callLog')}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder={t('manager.batchCalls.details.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8 text-sm w-48 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <SelectTrigger className="h-8 w-36 text-sm bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700">
                <Filter className="w-3 h-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t('manager.batchCalls.details.filter.all')}
                </SelectItem>
                <SelectItem value="answered">
                  {t('manager.batchCalls.details.filter.answered')}
                </SelectItem>
                <SelectItem value="failed">
                  {t('manager.batchCalls.details.filter.failed')}
                </SelectItem>
                <SelectItem value="pending">
                  {t('manager.batchCalls.details.filter.pending')}
                </SelectItem>
                <SelectItem value="skipped">
                  {t('manager.batchCalls.details.filter.skipped')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="text-xs text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800/50 uppercase font-medium border-b border-slate-200 dark:border-slate-800">
              <tr>
                <th className="px-6 py-3 w-12">#</th>
                <th className="px-6 py-3">
                  {t('manager.batchCalls.details.columns.number')}
                </th>
                <th className="px-6 py-3">
                  {t('manager.batchCalls.details.columns.status')}
                </th>
                <th className="px-6 py-3">
                  {t('manager.batchCalls.details.columns.duration')}
                </th>
                <th className="px-6 py-3">
                  {t('manager.batchCalls.details.columns.time')}
                </th>
                <th className="px-6 py-3 text-right">
                  {t('manager.batchCalls.details.columns.attempt')}
                </th>
                {/* Colonne chevron — pas de header, signal visuel de cliquabilité */}
                <th className="w-12" aria-hidden="true" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {paginated.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-slate-500 dark:text-slate-400"
                  >
                    <Phone className="w-8 h-8 mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                    <p className="font-medium">
                      {t('manager.batchCalls.details.noCalls')}
                    </p>
                    <p className="text-sm mt-1">
                      {searchQuery || statusFilter !== 'all'
                        ? t('manager.batchCalls.details.modifyFilters')
                        : t('manager.batchCalls.details.noCallsHint')}
                    </p>
                  </td>
                </tr>
              ) : (
                paginated.map((log, idx) => (
                  <tr
                    key={log.id}
                    onClick={() => setSelectedLog(log)}
                    className="group hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-2.5 text-slate-400 tabular-nums">
                      {(currentPage - 1) * PAGE_SIZE + idx + 1}
                    </td>
                    <td className="px-6 py-2.5 font-mono text-sm text-slate-900 dark:text-white">
                      {log.phone_number}
                    </td>
                    <td className="px-6 py-2.5">
                      <CallStatusBadge status={log.call_status} />
                    </td>
                    <td className="px-6 py-2.5 text-slate-600 dark:text-slate-400 tabular-nums">
                      {formatDuration(log.duration)}
                    </td>
                    <td className="px-6 py-2.5 text-slate-600 dark:text-slate-400">
                      {formatTime(log.created_at)}
                    </td>
                    <td className="px-6 py-2.5 text-right text-slate-600 dark:text-slate-400 tabular-nums">
                      {log.attempt_number}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <ChevronRight
                        className="w-4 h-4 inline-block text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-300 transition-colors"
                        aria-label={t('manager.batchCalls.details.viewDetails', 'Voir les détails')}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 flex items-center justify-end gap-4">
            <span className="text-sm text-slate-500 dark:text-slate-400">
              {(currentPage - 1) * PAGE_SIZE + 1}-
              {Math.min(currentPage * PAGE_SIZE, filteredLogs.length)}{' '}
              {t('manager.batchCalls.pagination.of')} {filteredLogs.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sheet de détails d'un appel */}
      <LogDetailSheet log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  )
}

// =============================================================================
// Sous-composants
// =============================================================================

function StatusBadge({ status }: { status: string }) {
  const { t } = useTranslation()
  const config: Record<string, string> = {
    completed: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30',
    in_progress: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
    scheduled: 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/30',
    cancelled: 'bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-300 border-red-200 dark:border-red-500/30',
    retry_scheduled: 'bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-500/30',
    retry_in_progress: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-500/30',
    draft: 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-700',
  }
  const cls = config[status] ?? config.draft
  return (
    <span className={cn('px-2.5 py-1 rounded-full text-xs font-medium border', cls)}>
      {t(`manager.batchCalls.statuses.${status}`, { defaultValue: status })}
    </span>
  )
}

function CallStatusBadge({ status }: { status: string }) {
  const dotColor: Record<string, string> = {
    answered: 'bg-emerald-500',
    voicemail: 'bg-yellow-500',
    no_answer: 'bg-amber-500',
    busy: 'bg-amber-500',
    failed: 'bg-red-500',
    skipped: 'bg-slate-400',
    calling: 'bg-blue-500 animate-pulse',
    pending: 'bg-slate-300',
  }
  return (
    <div className="flex items-center gap-2">
      <span className={cn('w-2 h-2 rounded-full', dotColor[status] ?? 'bg-slate-300')} />
      <span className="text-sm capitalize text-slate-700 dark:text-slate-300">
        {status.replace('_', ' ')}
      </span>
    </div>
  )
}

interface StatCardProps {
  icon: React.ReactNode
  label: string
  value: number | string
  pct?: number
  tone?: 'emerald' | 'amber' | 'red'
}

function StatCard({ icon, label, value, pct, tone }: StatCardProps) {
  const colorClass: Record<string, string> = {
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
    red: 'text-red-600 dark:text-red-400',
  }
  return (
    <div className="p-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 mb-2">
        {icon}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-baseline gap-2">
        <span
          className={cn(
            'text-3xl font-bold tracking-tight text-slate-900 dark:text-white tabular-nums',
            tone && colorClass[tone],
          )}
        >
          {value}
        </span>
        {pct != null && (
          <span
            className={cn(
              'text-sm font-medium opacity-70',
              tone ? colorClass[tone] : 'text-slate-500',
            )}
          >
            {pct}%
          </span>
        )}
      </div>
    </div>
  )
}

// Parse les transcripts ElevenLabs au format "[42s] agent: message" / "[45s] user: ..."
// Filtre les "None" qui apparaissent quand l'IA n'a pas dit grand-chose.
function parseTranscript(transcript: string) {
  if (!transcript) return [] as { time: string; role: 'agent' | 'user'; message: string }[]
  const out: { time: string; role: 'agent' | 'user'; message: string }[] = []
  for (const line of transcript.split('\n')) {
    const m = line.match(/\[(\d+)s\]\s+(agent|user):\s+(.+)/)
    if (m && m[3] !== 'None') {
      out.push({ time: `${m[1]}s`, role: m[2] as 'agent' | 'user', message: m[3] })
    }
  }
  return out
}

function formatAudioTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${String(s).padStart(2, '0')}`
}

function LogDetailSheet({
  log,
  onClose,
}: {
  log: BatchCallLog | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const { locale = 'fr' } = useParams<{ locale: string }>()
  const dateLocale = locale === 'fr' ? 'fr-CA' : 'en-CA'

  // Hooks AVANT tout early return — sinon "Rendered fewer hooks".
  const audioRef = useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [transcriptExpanded, setTranscriptExpanded] = useState(true)

  // Reset state audio quand on change de log (et au unmount)
  useEffect(() => {
    setIsPlaying(false)
    setCurrentTime(0)
  }, [log?.id])

  const detail = log?.ai_call_log_detail || null
  const messages = useMemo(
    () => (detail?.transcript ? parseTranscript(detail.transcript) : []),
    [detail?.transcript],
  )

  // L'audio_url stocké est l'URL directe ElevenLabs qui demande la clé API
  // dans le header xi-api-key (impossible depuis un <audio src=...>). On
  // utilise le proxy backend qui rajoute la clé et stream l'audio.
  const { data: audioSrc, isLoading: audioLoading } = useAICallAudio(
    detail?.audio_url ? detail.id : undefined,
  )
  // Cleanup du blob URL quand on change de log ou unmount.
  useEffect(() => {
    return () => {
      if (audioSrc) URL.revokeObjectURL(audioSrc)
    }
  }, [audioSrc])

  const handlePlayPause = () => {
    if (!audioRef.current) return
    if (isPlaying) audioRef.current.pause()
    else void audioRef.current.play()
    setIsPlaying(!isPlaying)
  }
  const handleSeek = (deltaSec: number) => {
    if (!audioRef.current) return
    audioRef.current.currentTime = Math.max(
      0,
      audioRef.current.currentTime + deltaSec,
    )
  }
  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime)
  }
  const handleProgressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!audioRef.current || !detail?.duration) return
    audioRef.current.currentTime =
      (Number(e.target.value) / 100) * detail.duration
  }

  return (
    <Sheet open={!!log} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-lg p-0 flex flex-col">
        {log && (
          <>
            <SheetHeader className="px-6 py-4 border-b dark:border-slate-800">
              <SheetTitle className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-600" />
                <span className="font-mono">{log.phone_number}</span>
              </SheetTitle>
            </SheetHeader>
            <div className="flex-1 overflow-y-auto">
              {/* Métadonnées de l'appel */}
              <div className="p-6 space-y-4 border-b dark:border-slate-800">
                <DetailRow
                  icon={<Calendar className="h-4 w-4" />}
                  label={t('manager.batchCalls.details.columns.time')}
                  value={
                    log.created_at
                      ? new Date(log.created_at).toLocaleString(dateLocale)
                      : '—'
                  }
                />
                <DetailRow
                  label={t('manager.batchCalls.details.columns.status')}
                  value={<CallStatusBadge status={log.call_status} />}
                />
                <DetailRow
                  label={t('manager.batchCalls.details.columns.duration')}
                  value={
                    log.duration
                      ? `${Math.floor(log.duration / 60)}:${String(log.duration % 60).padStart(2, '0')}`
                      : '—'
                  }
                />
                {/* Attempt + AI Call Log retirés : identifiants techniques
                    sans valeur pour l'utilisateur final (mêmes raisons que le
                    Call SID). L'historique des tentatives est visible par
                    prospect, pas besoin de le répéter ici. */}
              </div>

              {/* Résumé de la conversation */}
              {detail?.conversation_summary && (
                <div className="px-6 pt-5 pb-4 border-b dark:border-slate-800">
                  <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5" />
                    {t('manager.batchCalls.details.summary', 'Résumé')}
                  </h4>
                  <div
                    className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed bg-slate-50 dark:bg-slate-900/50 rounded-lg p-4 border border-slate-200 dark:border-slate-800"
                    dangerouslySetInnerHTML={{
                      __html: detail.conversation_summary
                        .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
                        .replace(/\n/g, '<br />'),
                    }}
                  />
                </div>
              )}

              {/* Audio Player — src vient du proxy backend (cf. useAICallAudio) */}
              {detail?.audio_url && (
                <div className="px-6 pt-5 pb-5 border-b dark:border-slate-800">
                  <h4 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <Volume2 className="w-3.5 h-3.5" />
                    {t('manager.batchCalls.details.audio', 'Enregistrement')}
                    {audioLoading && (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />
                    )}
                  </h4>
                  <div className="bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-900/50 dark:to-slate-900/30 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
                    <audio
                      ref={audioRef}
                      src={audioSrc}
                      onTimeUpdate={handleTimeUpdate}
                      onEnded={() => setIsPlaying(false)}
                      onLoadedMetadata={() => setCurrentTime(0)}
                    />
                    <div className="flex items-center gap-4">
                      <button
                        type="button"
                        onClick={handlePlayPause}
                        disabled={!audioSrc}
                        className="h-11 w-11 rounded-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white flex items-center justify-center shadow-lg shadow-blue-600/25 transition-all hover:scale-105 active:scale-95 disabled:hover:scale-100 flex-shrink-0"
                      >
                        {isPlaying ? (
                          <Pause className="h-4 w-4" />
                        ) : (
                          <Play className="h-4 w-4 ml-0.5" />
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="relative h-1.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"
                            style={{
                              width: `${detail.duration ? (currentTime / detail.duration) * 100 : 0}%`,
                            }}
                          />
                          <input
                            type="range"
                            min="0"
                            max="100"
                            value={
                              detail.duration
                                ? (currentTime / detail.duration) * 100
                                : 0
                            }
                            onChange={handleProgressChange}
                            className="absolute inset-0 w-full opacity-0 cursor-pointer"
                          />
                        </div>
                        <div className="flex items-center justify-between mt-1.5">
                          <span className="text-[11px] text-slate-400 font-mono tabular-nums">
                            {formatAudioTime(currentTime)}
                          </span>
                          <span className="text-[11px] text-slate-400 font-mono tabular-nums">
                            {formatAudioTime(detail.duration || 0)}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[11px] text-slate-500 hover:text-slate-700"
                        onClick={() => handleSeek(-10)}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" />
                        10s
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-[11px] text-slate-500 hover:text-slate-700"
                        onClick={() => handleSeek(10)}
                      >
                        <RotateCw className="h-3 w-3 mr-1" />
                        10s
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Transcript — bulles user/agent */}
              {detail?.transcript && messages.length > 0 && (
                <div className="flex flex-col">
                  <button
                    type="button"
                    onClick={() => setTranscriptExpanded((v) => !v)}
                    className="w-full flex items-center justify-between px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:bg-slate-50 dark:hover:bg-slate-900/50 transition-colors"
                  >
                    <span className="flex items-center gap-1.5">
                      <MessageSquareText className="w-3.5 h-3.5" />
                      {t('manager.batchCalls.details.transcript', 'Transcription')}
                      <span className="text-slate-400 font-normal ml-1">
                        ({messages.length} messages)
                      </span>
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 transition-transform',
                        transcriptExpanded && 'rotate-180',
                      )}
                    />
                  </button>
                  {transcriptExpanded && (
                    <>
                      <div className="px-5 py-5 space-y-3 bg-gradient-to-b from-slate-50/50 to-white dark:from-slate-900/30 dark:to-transparent">
                        {messages.map((msg, idx) => (
                          <div
                            key={idx}
                            className={cn(
                              'flex',
                              msg.role === 'agent' ? 'justify-start' : 'justify-end',
                            )}
                          >
                            <div className="max-w-[75%]">
                              <div
                                className={cn(
                                  'flex items-center gap-1.5 mb-1',
                                  msg.role === 'user' && 'flex-row-reverse',
                                )}
                              >
                                <div
                                  className={cn(
                                    'w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold',
                                    msg.role === 'agent'
                                      ? 'bg-blue-600 text-white shadow-sm shadow-blue-500/20'
                                      : 'bg-slate-300 text-slate-600',
                                  )}
                                >
                                  {msg.role === 'agent' ? 'IA' : 'C'}
                                </div>
                                <span className="text-[10px] text-slate-400 font-medium">
                                  {msg.role === 'agent'
                                    ? t('manager.batchCalls.details.agent', 'Agent')
                                    : t('manager.batchCalls.details.client', 'Client')}
                                </span>
                                <span className="text-[10px] text-slate-300 tabular-nums">
                                  {msg.time}
                                </span>
                              </div>
                              <div
                                className={cn(
                                  'rounded-2xl px-3.5 py-2.5 text-[13px] leading-relaxed',
                                  msg.role === 'agent'
                                    ? 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 shadow-sm'
                                    : 'bg-blue-600 text-white shadow-md shadow-blue-600/15',
                                )}
                              >
                                {msg.message}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/30 px-5 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Mic className="h-3.5 w-3.5 text-slate-400" />
                          <p className="text-[11px] text-slate-500 dark:text-slate-400 font-medium">
                            {messages.length} messages
                          </p>
                        </div>
                        <p className="text-[11px] text-slate-400">
                          {t('manager.batchCalls.details.columns.duration')} :{' '}
                          {log.duration
                            ? `${Math.floor(log.duration / 60)}:${String(log.duration % 60).padStart(2, '0')}`
                            : '—'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Aucune donnée IA */}
              {log.ai_call_log &&
                !detail?.conversation_summary &&
                !detail?.transcript &&
                !detail?.audio_url && (
                  <div className="text-center py-10 text-slate-400 dark:text-slate-500 px-6">
                    <MessageSquareText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">
                      {t(
                        'manager.batchCalls.details.noAiData',
                        'Aucune donnée IA disponible pour cet appel.',
                      )}
                    </p>
                  </div>
                )}
            </div>

            <div className="px-6 py-4 border-t dark:border-slate-800 flex justify-end">
              <Button type="button" variant="outline" onClick={onClose}>
                <X className="h-4 w-4 mr-2" />
                {t('common.cancel')}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

function DetailRow({
  icon,
  label,
  value,
}: {
  icon?: React.ReactNode
  label: string
  value: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
        {icon}
        <span>{label}</span>
      </div>
      <div className="text-sm text-slate-900 dark:text-white">{value}</div>
    </div>
  )
}

// Évite l'erreur eslint si BatchCall importé jamais utilisé.
export type { BatchCall as _BatchCall }
